import { useCognitoAuth } from '@/contexts/cognitoAuthContext'
import { DashboardExperiments } from '@/experiments'
import { useTemplateManagementPermissions } from '@/hooks/permissions/templateManagement/useTemplateManagementPermissions'
import { useFlag } from '@/hooks/useFlag'
import { PATHS } from '@/routes/PATHS'
import { CoreLevelFeatureNames } from '@/services/types/permissions/permissionFeatures/mockCorePermissionFeature'
import { PremiumFeatures } from '@/services/types/premiumFeatures/premiumFeatures'
import { useCorePermissions } from '@/stores/permissions/permissions'
import { usePremiumFeaturesPermissions } from '@/stores/permissions/premiumFeatures'
import {
  BellOutlined,
  CommentOutlined,
  FileTextOutlined,
  MessageOutlined,
  StarOutlined,
} from '@ant-design/icons'
import { MessageSquarePlus, RectangleEllipsis } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const ICON_SIZE = 14

export function useReviewsSidebarItems() {
  const { t } = useTranslation()
  const { isMarqiiUser } = useCognitoAuth()
  const reviewGenerationFlag = useFlag(DashboardExperiments.reviewGeneration)
  const { shouldShowTemplatesManagement } = useTemplateManagementPermissions()

  const responsePromptsPermission = useCorePermissions(CoreLevelFeatureNames.promptManagement)
  const labelManagementPermission = useCorePermissions(CoreLevelFeatureNames.labelManagement)
  const listingsManagementPermission = useCorePermissions(CoreLevelFeatureNames.listingsManagement)

  const premiumFeaturesPermissions = usePremiumFeaturesPermissions()
  const hasManagedReviewsExternalFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ManagedReviewsExternal,
  )
  const hasManagedReviewsInternalFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ManagedReviewsInternal,
  )
  const hasReviewMonitoringFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewMonitoring,
  )
  const hasWidgetReviewFormsFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.WidgetReviewForms,
  )
  const hasReviewGenerationFeature = premiumFeaturesPermissions.includes(
    PremiumFeatures.ReviewGeneration,
  )

  const hasResponsePromptPermission = !!responsePromptsPermission?.edit
  const hasLabelManagementPermission = !!labelManagementPermission?.edit
  const shouldHideListingsPageItem = !!listingsManagementPermission?.hide

  const hasManagedReviewsInternalFeatureAndIsMarqiiUser =
    isMarqiiUser && hasManagedReviewsInternalFeature

  const shouldShowResponsePrompts =
    (hasManagedReviewsExternalFeature || hasManagedReviewsInternalFeatureAndIsMarqiiUser) &&
    hasResponsePromptPermission

  const shouldShowLabelManagementPage = hasReviewMonitoringFeature && hasLabelManagementPermission

  const shouldShowFirstPartyReviewsUpsellPage =
    hasReviewMonitoringFeature && !hasWidgetReviewFormsFeature
  const hasFirstPartyReviewsPermission = !shouldHideListingsPageItem
  const shouldShowFirstPartyReviewsPage =
    shouldShowFirstPartyReviewsUpsellPage ||
    (hasFirstPartyReviewsPermission && hasWidgetReviewFormsFeature)
  const firstPartyReviewsPath = shouldShowFirstPartyReviewsUpsellPage
    ? PATHS.firstPartyReviewsUpsell
    : PATHS.firstPartyReviews

  const shouldShowReviewGenerationPage = hasReviewGenerationFeature && reviewGenerationFlag

  const allItems = [
    {
      key: PATHS.reviewsPage,
      icon: <CommentOutlined />,
      label: t('sidebarItems.monitoring'),
    },
    {
      key: PATHS.reviewGenerationPage,
      icon: <MessageSquarePlus size={ICON_SIZE} />,
      label: t('sidebarItems.reviewGeneration'),
    },
    {
      key: firstPartyReviewsPath,
      icon: <RectangleEllipsis size={ICON_SIZE} />,
      label: t('sidebarItems.firstPartyReviews'),
    },
    {
      key: PATHS.reviewResponsePromptsPage,
      icon: <MessageOutlined />,
      label: t('sidebarItems.responsePrompts'),
    },
    {
      key: PATHS.reviewTemplatesPage,
      icon: <FileTextOutlined />,
      label: t('sidebarItems.templates'),
    },
    {
      key: PATHS.reviewLabelsPage,
      icon: <StarOutlined />,
      label: t('sidebarItems.labels'),
    },
    {
      key: PATHS.reviewNotificationsPage,
      icon: <BellOutlined />,
      label: t('sidebarItems.notifications'),
    },
  ]

  return allItems
    .filter((item) => shouldShowResponsePrompts || item.key !== PATHS.reviewResponsePromptsPage)
    .filter((item) => shouldShowLabelManagementPage || item.key !== PATHS.reviewLabelsPage)
    .filter((item) => shouldShowTemplatesManagement || item.key !== PATHS.reviewTemplatesPage)
    .filter((item) => shouldShowFirstPartyReviewsPage || item.key !== PATHS.firstPartyReviews)
    .filter((item) => shouldShowReviewGenerationPage || item.key !== PATHS.reviewGenerationPage)
}
